import { makeServiceHeaders } from "@coworker/app/src/core/hooks/fetch.helpers";
import { CORE_SERVICE_URL } from "@coworker/app/src/hooks/API/core.service";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BasePermission } from "../types/BasePermission";
import { CountryPermission } from "../types/CountryPermission";
import { StorePermission } from "../types/StorePermission";
import { createAuthorizationHeader } from "../../task-management/services/fetchHelper";

async function getAllPermissions(): Promise<BasePermission[]> {
  const url = `${CORE_SERVICE_URL}/permissions`;
  const response = await fetch(url, {
    method: "GET",
    headers: await makeServiceHeaders(),
  });

  if (!response.ok) {
    console.error(
      "Failed to fetch permissions",
      response.status,
      response.statusText
    );
    throw new Error(`Failed to fetch permissions: ${response.status}`);
  }

  return await response.json();
}

async function createPermission(data: {
  code: string;
  description: string;
}): Promise<BasePermission> {
  const url = `${CORE_SERVICE_URL}/permissions`;
  const response = await fetch(url, {
    method: "POST",
    headers: await makeServiceHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    console.error(
      "Failed to create permission",
      response.status,
      response.statusText
    );
    throw new Error(`Failed to create permission: ${response.status}`);
  }

  return await response.json();
}

async function getAllCountryPermissions(): Promise<CountryPermission[]> {
  const url = `${CORE_SERVICE_URL}/permissions/country-permissions`;
  const response = await fetch(url, {
    method: "GET",
    headers: await makeServiceHeaders(),
  });

  if (!response.ok) {
    console.error(
      "Failed to fetch country permissions",
      response.status,
      response.statusText
    );
    throw new Error(`Failed to fetch country permissions: ${response.status}`);
  }

  return await response.json();
}

async function createCountryPermission(data: {
  permission_id: string;
  country_id: string;
  value: boolean;
}): Promise<CountryPermission> {
  const url = `${CORE_SERVICE_URL}/permissions/country-permissions`;
  const response = await fetch(url, {
    method: "POST",
    headers: await makeServiceHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    console.error(
      "Failed to create country permission",
      response.status,
      response.statusText
    );
    throw new Error(`Failed to create country permission: ${response.status}`);
  }

  return await response.json();
}

async function updateCountryPermission(
  id: number,
  data: { value: boolean }
): Promise<CountryPermission> {
  const url = `${CORE_SERVICE_URL}/permissions/country-permissions/${id}`;
  const response = await fetch(url, {
    method: "PUT",
    headers: await makeServiceHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    console.error(
      "Failed to update country permission",
      response.status,
      response.statusText
    );
    throw new Error(`Failed to update country permission: ${response.status}`);
  }

  return await response.json();
}

async function deleteCountryPermission(
  id: number
): Promise<{ success: boolean }> {
  const url = `${CORE_SERVICE_URL}/permissions/country-permissions/${id}`;
  const headers = await createAuthorizationHeader();
  const response = await fetch(url, {
    method: "DELETE",
    headers,
  });

  if (!response.ok) {
    console.error(
      "Failed to delete country permission",
      response.status,
      response.statusText
    );
    throw new Error(`Failed to delete country permission: ${response.status}`);
  }

  return await response.json();
}

async function getAllStorePermissions(): Promise<StorePermission[]> {
  const url = `${CORE_SERVICE_URL}/permissions/store-permissions`;
  const response = await fetch(url, {
    method: "GET",
    headers: await makeServiceHeaders(),
  });

  if (!response.ok) {
    console.error(
      "Failed to fetch store permissions",
      response.status,
      response.statusText
    );
    throw new Error(`Failed to fetch store permissions: ${response.status}`);
  }

  return await response.json();
}

async function createStorePermission(data: {
  permission_id: string;
  store_id: string;
  value: boolean;
}): Promise<StorePermission> {
  const url = `${CORE_SERVICE_URL}/permissions/store-permissions`;
  const response = await fetch(url, {
    method: "POST",
    headers: await makeServiceHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    console.error(
      "Failed to create store permission",
      response.status,
      response.statusText
    );
    throw new Error(`Failed to create store permission: ${response.status}`);
  }

  return await response.json();
}

async function updateStorePermission(
  id: number,
  data: { value: boolean }
): Promise<StorePermission> {
  const url = `${CORE_SERVICE_URL}/permissions/store-permissions/${id}`;
  const response = await fetch(url, {
    method: "PUT",
    headers: await makeServiceHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    console.error(
      "Failed to update store permission",
      response.status,
      response.statusText
    );
    throw new Error(`Failed to update store permission: ${response.status}`);
  }

  return await response.json();
}

async function deleteStorePermission(
  id: number
): Promise<{ success: boolean }> {
  const url = `${CORE_SERVICE_URL}/permissions/store-permissions/${id}`;
  const response = await fetch(url, {
    method: "DELETE",
    headers: await createAuthorizationHeader(),
  });

  if (!response.ok) {
    console.error(
      "Failed to delete store permission",
      response.status,
      response.statusText
    );
    throw new Error(`Failed to delete store permission: ${response.status}`);
  }

  return await response.json();
}

// ==================== React Query Hooks ====================

// Permission Hooks
export const useGetAllPermissions = () => {
  return useQuery({
    queryKey: ["permissions"],
    queryFn: getAllPermissions,
  });
};

export const useCreatePermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPermission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["permissions"],
        refetchType: "all",
      });
    },
  });
};

// Country Permission Hooks
export const useGetAllCountryPermissions = () => {
  return useQuery({
    queryKey: ["country-permissions"],
    queryFn: getAllCountryPermissions,
  });
};
export const useCreateCountryPermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCountryPermission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["country-permissions"],
        refetchType: "all",
      });
    },
  });
};

export const useUpdateCountryPermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { value: boolean } }) =>
      updateCountryPermission(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["country-permissions"],
        refetchType: "all",
      });
    },
  });
};

export const useDeleteCountryPermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCountryPermission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["country-permissions"],
        refetchType: "all",
      });
    },
  });
};

// Store Permission Hooks
export const useGetAllStorePermissions = () => {
  return useQuery({
    queryKey: ["store-permissions"],
    queryFn: getAllStorePermissions,
  });
};

export const useCreateStorePermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createStorePermission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["store-permissions"],
        refetchType: "all",
      });
    },
  });
};

export const useUpdateStorePermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { value: boolean } }) =>
      updateStorePermission(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["store-permissions"],
        refetchType: "all",
      });
    },
  });
};

export const useDeleteStorePermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteStorePermission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["store-permissions"],
        refetchType: "all",
      });
    },
  });
};

// ==================== Refresh User Scoped Permissions ====================

async function refreshUserScopedPermissions(): Promise<{ success: boolean }> {
  const url = `${CORE_SERVICE_URL}/permissions/user-scoped-permissions/refresh`;
  const response = await fetch(url, {
    method: "POST",
    headers: await makeServiceHeaders(),
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    console.error(
      "Failed to refresh user scoped permissions",
      response.status,
      response.statusText
    );
    throw new Error(
      `Failed to refresh user scoped permissions: ${response.status}`
    );
  }

  return await response.json();
}

export const useRefreshUserScopedPermissions = () => {
  return useMutation({
    mutationFn: refreshUserScopedPermissions,
  });
};
